import type { OwnerListItem, UpsertOwnerInput } from '@sea-snag/shared';
import {
  Button,
  Card,
  CardContent,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Copy, Mail, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { RequireOwnerOrAdmin } from '@/components/require-owner-or-admin';

import { ConfirmDialog } from '../../components/confirm-dialog';
import { LoadingState } from '../../components/loading-state';
import { useOwnerMutations, useOwners } from '../../hooks/use-owners';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import { boatsApi } from '../../lib/api-client';
import { ownerFormPayload } from '../../lib/owner-form';
import { queryKeys } from '../../lib/query-keys';

export const Route = createFileRoute('/$orgPid/owners')({
  component: GuardedOwnersPage,
});

/** Members are redirected to the dashboard; the API refuses them too. */
function GuardedOwnersPage() {
  return (
    <RequireOwnerOrAdmin>
      <OwnersPage />
    </RequireOwnerOrAdmin>
  );
}

function OwnersPage() {
  const { organization, isLoading } = useRequiredOrganization();
  if (isLoading) return <LoadingState />;
  return <OwnersContent orgPid={organization.pid} />;
}

function ownerLink(token: string | null): string | null {
  return token ? `${window.location.origin}/owner-status/${token}` : null;
}

function OwnersContent({ orgPid }: { orgPid: string }) {
  const { data: owners = [], isLoading } = useOwners(orgPid);
  const m = useOwnerMutations(orgPid);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<OwnerListItem | null>(null);
  // Editing replaces the name, email and boats in one go (#216); before, an owner
  // had to be deleted and recreated to add or remove a boat.
  const [editTarget, setEditTarget] = useState<OwnerListItem | null>(null);

  function copyLink(token: string | null) {
    const link = ownerLink(token);
    if (!link) return;
    void navigator.clipboard.writeText(link).then(
      () => toast.success('Link copied'),
      () => toast.error('Failed to copy')
    );
  }

  if (isLoading) return <LoadingState />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Owners</h1>
          <p className="text-muted-foreground">
            Create boat owners and share a no-login status link.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>Add owner</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New owner</DialogTitle>
              <DialogDescription>
                They&apos;ll get a private link showing their boats&apos; bookings and earnings — no
                login required.
              </DialogDescription>
            </DialogHeader>
            <OwnerForm
              orgPid={orgPid}
              isPending={m.create.isPending}
              submitLabel="Create owner"
              onSubmit={(data) => m.create.mutate(data, { onSuccess: () => setCreateOpen(false) })}
            />
          </DialogContent>
        </Dialog>
      </div>

      {owners.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No owners yet. Add one to generate a status link.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {owners.map((o) => (
            <Card key={o.pid}>
              <CardContent className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="font-medium">{o.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {[o.email, o.boats.map((b) => b.name).join(', ') || 'no boats']
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  <div className="mt-0.5 text-xs">
                    {o.token ? (
                      <span className="text-green-600">Link active</span>
                    ) : (
                      <span className="text-muted-foreground">No active link</span>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!o.token}
                    onClick={() => copyLink(o.token)}
                  >
                    <Copy className="mr-1 size-3.5" /> Copy
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!o.token || !o.email || m.sendEmail.isPending}
                    title={o.email ? undefined : 'No email address: copy the link instead'}
                    onClick={() => m.sendEmail.mutate(o.pid)}
                  >
                    <Mail className="mr-1 size-3.5" /> Email
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditTarget(o)}>
                    <Pencil className="mr-1 size-3.5" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={m.regenerate.isPending}
                    onClick={() => m.regenerate.mutate(o.pid)}
                  >
                    Regenerate
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!o.token}
                    onClick={() => m.revoke.mutate(o.pid)}
                  >
                    Revoke
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${o.name}`}
                    onClick={() => setDeleteTarget(o)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit owner</DialogTitle>
            <DialogDescription>
              Change the name, email or boats. Their status link stays the same.
            </DialogDescription>
          </DialogHeader>
          {editTarget && (
            <OwnerForm
              key={editTarget.pid}
              orgPid={orgPid}
              isPending={m.update.isPending}
              submitLabel="Save changes"
              initial={{
                name: editTarget.name,
                email: editTarget.email ?? '',
                boatPids: editTarget.boats.map((b) => b.boatPid),
              }}
              onSubmit={(data) =>
                m.update.mutate(
                  { pid: editTarget.pid, data },
                  { onSuccess: () => setEditTarget(null) }
                )
              }
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete owner"
        description={`Delete ${deleteTarget?.name ?? ''}? Their status link will stop working.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) m.remove.mutate(deleteTarget.pid);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}

function OwnerForm({
  orgPid,
  isPending,
  submitLabel,
  initial,
  onSubmit,
}: {
  orgPid: string;
  isPending: boolean;
  submitLabel: string;
  /** An existing owner's values, when editing. */
  initial?: { name: string; email: string; boatPids: string[] };
  onSubmit: (data: UpsertOwnerInput) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [boatPids, setBoatPids] = useState<string[]>(initial?.boatPids ?? []);

  const { data: boatsPage } = useQuery({
    queryKey: queryKeys.boats.list(orgPid, 'owners-picker'),
    queryFn: () => boatsApi.list({ limit: 100 }),
  });
  const boats = boatsPage?.data ?? [];

  function toggleBoat(pid: string, checked: boolean) {
    setBoatPids((prev) =>
      checked ? Array.from(new Set([...prev, pid])) : prev.filter((p) => p !== pid)
    );
  }

  return (
    <form
      className="space-y-4"
      aria-label="Owner form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(ownerFormPayload({ name, email, boatPids }));
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="owner-name">Name</Label>
        <Input id="owner-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="owner-email">Email (optional)</Label>
        <Input
          id="owner-email"
          type="email"
          placeholder="owner@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label>Boats this owner can see</Label>
        {boats.length === 0 ? (
          <p className="text-sm text-muted-foreground">No boats in your fleet yet.</p>
        ) : (
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
            {boats.map((boat) => {
              const checked = boatPids.includes(boat.id);
              return (
                <button
                  type="button"
                  key={boat.id}
                  role="checkbox"
                  aria-checked={checked}
                  onClick={() => toggleBoat(boat.id, !checked)}
                  className="flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-accent"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-primary text-primary-foreground',
                      checked && 'bg-primary'
                    )}
                  >
                    {checked && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="size-3"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  <span className="flex-1 truncate text-sm">
                    {boat.name}
                    {boat.model && (
                      <span className="ml-2 text-xs text-muted-foreground">{boat.model}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <Button type="submit" className="w-full" disabled={isPending}>
        {isPending ? 'Saving…' : submitLabel}
      </Button>
    </form>
  );
}
